import React, { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import ContactCTAs from "@/components/ContactCTAs";
import { api } from "@/lib/api";
import { Phone, MapPin, Clock, Users, Car } from "@phosphor-icons/react";

const HERO = "https://images.pexels.com/photos/12813576/pexels-photo-12813576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SUV = "https://images.unsplash.com/photo-1735620731955-b047a7122892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMFNVVnxlbnwwfHx8fDE3ODQ0OTc1MzR8MA&ixlib=rb-4.1.0&q=85";
const TESTIMONIAL_BG = "https://images.unsplash.com/photo-1683782440921-823862401342?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxhcnViYSUyMGJlYWNoJTIwc3Vuc2V0fGVufDB8fHx8MTc4NDQ5NzUzNHww&ixlib=rb-4.1.0&q=85";

const TESTIMONIALS = [
  { q: "The driver was waiting with a sign right at arrivals. Smooth pickup, quiet ride, on time to Eagle Beach.", a: "Traveler · Aruba" },
  { q: "Called from the gate. Confirmed in under two minutes. Bags loaded before I finished a bottle of water.", a: "Traveler · Aruba" },
  { q: "Exactly the standard I expected — clean vehicle, professional driver, no small talk unless welcomed.", a: "Traveler · Aruba" },
];

export default function Aruba() {
  const [tracking, setTracking] = useState(null);
  useEffect(() => {
    api.get("/tracking/aruba").then((r) => setTracking(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-cream min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Aruba coastline at twilight" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-deep/75" />
          <div className="absolute inset-0 grain opacity-25" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.35em] text-gold-bright mb-6" data-testid="aruba-eyebrow">
                — Aruba · Private Transfer
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-offwhite leading-[0.95]" data-testid="aruba-headline">
                One number.<br />
                <em className="text-gold-bright not-italic">One driver.</em> All of Aruba.
              </h1>
              <p className="mt-6 text-offwhite/80 text-base sm:text-lg max-w-xl leading-relaxed">
                Airport pickup, hotel or villa transfer, cruise-port shuttle, San Nicolas day trips —
                dispatched live to our current licensed Aruba operator. Call, WhatsApp, or reserve ahead.
              </p>
              <p className="mt-3 text-offwhite/60 text-sm max-w-xl leading-relaxed" data-testid="aruba-rental-line">
                Staying at a villa or Airbnb? We'll find you just as easily as a hotel lobby.
              </p>
              <p className="mt-2 text-offwhite/60 text-sm max-w-xl leading-relaxed" data-testid="aruba-dining-line">
                Dinner reservation across the island? No rental car, no valet stress — just a driver
                waiting when you're ready.
              </p>
              <ContactCTAs tracking={tracking} className="mt-8" />
            </div>
            <div className="lg:col-span-5" data-testid="aruba-lead-form-slot">
              <LeadForm islandSlug="aruba" variant="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Service coverage */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-teal mb-4">Coverage</div>
            <h2 className="font-display text-4xl text-navy leading-tight">Anywhere on the island.</h2>
            <p className="mt-5 text-navy/70 leading-relaxed">
              Our Aruba operator covers the airport, hotels, resorts, and vacation rentals across the island —
              from Palm Beach and Eagle Beach to downtown Oranjestad and the cruise port. We go beyond the hotel
              strip: San Nicolas art district, Arikok National Park, Arashi Beach, California Lighthouse.
            </p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-5">
            {[
              { icon: <MapPin size={24} weight="thin" />, t: "Aruba Intl. Airport (AUA)", s: "Terminal-side pickup with a welcome sign" },
              { icon: <MapPin size={24} weight="thin" />, t: "Hotels & Resorts", s: "Palm Beach and Eagle Beach — Divi, Marriott, Hyatt, Ritz, Bucuti, Manchebo" },
              { icon: <MapPin size={24} weight="thin" />, t: "Villas & Vacation Rentals", s: "Airbnb, private villas, condos — any address, no lobby required" },
              { icon: <MapPin size={24} weight="thin" />, t: "Oranjestad + Cruise Port", s: "Downtown transfers and shore excursions" },
              { icon: <MapPin size={24} weight="thin" />, t: "San Nicolas Art District", s: "Murals, galleries, and the annual Aruba Art Fair (Sept 11–13)" },
              { icon: <MapPin size={24} weight="thin" />, t: "Arikok & the North Coast", s: "Natural pool, Arashi Beach, California Lighthouse" },
            ].map((it, i) => (
              <div key={i} className="border border-cream2 p-6 bg-cream hover:border-gold transition-colors">
                <div className="text-teal mb-3">{it.icon}</div>
                <div className="font-display text-xl text-navy">{it.t}</div>
                <div className="text-navy/60 text-sm mt-1">{it.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle classes */}
      <section className="bg-navy-deep text-offwhite">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={SUV} alt="Premium black SUV" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold-bright mb-4">Fleet</div>
            <h2 className="font-display text-5xl leading-tight">Cabins built for the arrival.</h2>
            <div className="mt-8 space-y-6">
              <div className="border-t border-navy-soft pt-6 flex items-start gap-6">
                <Car size={28} weight="thin" className="text-gold-bright shrink-0" />
                <div>
                  <div className="font-display text-2xl">Executive SUV</div>
                  <div className="text-offwhite/60 text-sm mt-1">Up to 4 passengers · 4 large bags · child seat on request</div>
                </div>
              </div>
              <div className="border-t border-navy-soft pt-6 flex items-start gap-6">
                <Users size={28} weight="thin" className="text-gold-bright shrink-0" />
                <div>
                  <div className="font-display text-2xl">Private Van</div>
                  <div className="text-offwhite/60 text-sm mt-1">Up to 8 passengers · groups, families, extended luggage</div>
                </div>
              </div>
              <div className="border-t border-navy-soft pt-6 flex items-start gap-6">
                <Clock size={28} weight="thin" className="text-gold-bright shrink-0" />
                <div>
                  <div className="font-display text-2xl">On-Demand Charter</div>
                  <div className="text-offwhite/60 text-sm mt-1">Half-day or full-day driver + vehicle · restaurants, excursions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={TESTIMONIAL_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-deep/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <div className="text-[11px] uppercase tracking-[0.35em] text-gold-bright mb-10 text-offwhite/80">— From recent travelers</div>
          <div className="grid md:grid-cols-3 gap-10">
            {TESTIMONIALS.map((t, i) => (
              <blockquote key={i} className="border-l border-gold-bright/50 pl-6" data-testid={`testimonial-${i}`}>
                <p className="font-display text-2xl text-offwhite leading-snug">"{t.q}"</p>
                <footer className="mt-6 text-[10px] uppercase tracking-[0.28em] text-gold-bright">{t.a}</footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-24 text-center">
        <div className="text-[11px] uppercase tracking-[0.35em] text-teal mb-6">Ready when you are</div>
        <h2 className="font-display text-5xl sm:text-6xl text-navy leading-[1]">
          Land. Call. <em className="text-gold not-italic">Ride.</em>
        </h2>
        <p className="mt-4 text-navy/60">Book now — or reserve ahead of your trip.</p>
        {tracking && (
          <a
            href={`tel:${tracking.number_e164}`}
            data-testid="aruba-final-cta"
            className="mt-10 inline-flex items-center gap-4 bg-navy text-cream px-10 py-5 text-sm uppercase tracking-[0.28em] hover:bg-gold hover:text-navy transition-colors"
          >
            <Phone size={20} weight="thin" />
            {tracking.display}
          </a>
        )}
      </section>

      <Footer />
    </div>
  );
}
